/**
 * useNotificationLogs Hook - 알림 이력 상태 관리
 * 알림 이력 조회, 필터링, 페이지네이션 로직
 */

import { useState, useEffect, useCallback } from 'react';
import {
  notificationLogsRepository,
  NotificationLog,
  NotificationStats,
} from '../repositories/NotificationLogsRepository';

export const useNotificationLogs = () => {
  const [logs, setLogs] = useState<NotificationLog[]>([]);
  const [stats, setStats] = useState<NotificationStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 필터 상태
  const [selectedType, setSelectedType] = useState<string>('all');
  const [selectedDays, setSelectedDays] = useState<number>(7);
  const [searchQuery, setSearchQuery] = useState<string>('');
  // 입력 중인 글자마다 서버를 때리지 않도록 조금 늦춘 값. 실제 조회는 이걸로 한다
  const [appliedSearch, setAppliedSearch] = useState<string>('');

  // 페이지네이션 상태
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [limit] = useState(50);

  // 상세 모달 상태
  const [selectedLog, setSelectedLog] = useState<NotificationLog | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  /**
   * 알림 이력 로드
   */
  const loadNotificationLogs = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const offset = (currentPage - 1) * limit;
      const params: any = {
        days: selectedDays,
        limit,
        offset,
      };

      if (selectedType !== 'all') {
        params.notification_type = selectedType;
      }
      if (appliedSearch) {
        params.search = appliedSearch;
      }

      const response = await notificationLogsRepository.getNotificationLogs(params);

      setLogs(response.logs);
      setTotalCount(response.total);
    } catch (err: any) {
      setError(err.message || '알림 이력을 불러오는데 실패했습니다.');
      console.error('Failed to load notification logs:', err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, limit, selectedType, selectedDays, appliedSearch]);

  useEffect(() => {
    const t = setTimeout(() => {
      setAppliedSearch(searchQuery.trim());
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  /**
   * 통계 로드
   */
  const loadStats = useCallback(async () => {
    try {
      const statsData = await notificationLogsRepository.getNotificationStats(selectedDays);
      setStats(statsData);
    } catch (err: any) {
      console.error('Failed to load notification stats:', err);
    }
  }, [selectedDays]);

  /**
   * 초기 로드 및 필터 변경 시 재로드
   */
  useEffect(() => {
    loadNotificationLogs();
  }, [loadNotificationLogs]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  /**
   * 필터 변경 핸들러
   */
  const handleTypeFilterChange = (type: string) => {
    setSelectedType(type);
    setCurrentPage(1); // 필터 변경 시 첫 페이지로
  };

  const handleDaysFilterChange = (days: number) => {
    setSelectedDays(days);
    setCurrentPage(1);
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
  };

  /**
   * 페이지 변경
   */
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  /**
   * 알림 상세 모달 열기
   */
  const openDetailModal = (log: NotificationLog) => {
    setSelectedLog(log);
    setIsDetailModalOpen(true);
  };

  /**
   * 알림 상세 모달 닫기
   */
  const closeDetailModal = () => {
    setSelectedLog(null);
    setIsDetailModalOpen(false);
  };

  return {
    // 데이터
    logs,
    stats,
    loading,
    error,

    // 필터 상태
    selectedType,
    selectedDays,
    searchQuery,

    // 페이지네이션
    currentPage,
    totalCount,
    limit,
    totalPages: Math.ceil(totalCount / limit),

    // 모달 상태
    selectedLog,
    isDetailModalOpen,

    // 액션
    handleTypeFilterChange,
    handleDaysFilterChange,
    handleSearchChange,
    handlePageChange,
    openDetailModal,
    closeDetailModal,
    refresh: loadNotificationLogs,
  };
};
